import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string[];
}

const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
  const location = useLocation();
  const [session, setSession] = useState<Session | null>(null);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [emailConfirmed, setEmailConfirmed] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        if (session?.user) {
          fetchUserData(session.user.id);
        } else {
          setEmailConfirmed(null);
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        fetchUserData(session.user.id);
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserData = async (userId: string) => {
    try {
      // Fetch user roles and email confirmation status
      const [rolesResult, profileResult] = await Promise.all([
        supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId),
        supabase
          .from("profiles")
          .select("email_confirmed")
          .eq("id", userId)
          .single()
      ]);

      if (!rolesResult.error && rolesResult.data && rolesResult.data.length > 0) {
        setUserRoles(rolesResult.data.map((r) => r.role));
      } else {
        setUserRoles([]);
      }

      if (!profileResult.error && profileResult.data) {
        // Check if email is confirmed OR if user is an OAuth provider (Google, etc.)
        // OAuth users have their email verified by the provider
        const { data: { user } } = await supabase.auth.getUser();
        const isOAuthUser = user?.app_metadata?.provider && user.app_metadata.provider !== "email";
        
        setEmailConfirmed(profileResult.data.email_confirmed === true || !!isOAuthUser);
      } else {
        setEmailConfirmed(false);
      }
    } catch (e) {
      console.error("Error fetching user data:", e);
      setUserRoles([]);
      setEmailConfirmed(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!session) {
    const redirectTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?redirect=${redirectTo}`} replace />;
  }

  // Verificar se o email foi confirmado (dado vindo do banco de dados)
  if (session.user && emailConfirmed === false) {
    const email = session.user.email || "";
    if (email) {
      localStorage.setItem("pending_email_confirmation", email);
    }
    return <Navigate to="/onboarding" replace />;
  }

  if (requiredRole && !requiredRole.some((role) => userRoles.includes(role)) && !userRoles.includes("super_admin")) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold">403</h1>
          <p className="text-xl text-muted-foreground">Acesso não autorizado</p>
          <p className="text-sm text-muted-foreground">
            Suas permissões: {userRoles.join(", ") || "Nenhuma"}
          </p>
          <a href="/" className="text-primary underline">
            Voltar ao início
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
